import React, { useState } from 'react';
import { useUserContext } from '../config/UserContext';
import DeviceSelector from './DeviceSelector';
import { useKeycloak } from '../config/KeycloakContext';
import type { Device as DeviceType } from '../../../shared/types/models/device';
import type { EntityEnrollment } from '../../../shared/types/models/entity';
import CameraModal from './CameraModal';
import EntityListComponent from './EntityListComponent';
import { DOCUMENT_TYPE } from '../../../shared/types/models/document';

interface BiometricCardProps {
  onTakeBiometrics: (entityId: string) => void;
  onBack?: () => void;
  onSaveDraft: () => void;
  onSubmit: () => void;
  creatingLien?: boolean;
}

const BiometricsCard: React.FC<BiometricCardProps> = ({
  onTakeBiometrics,
  onBack,
  onSaveDraft,
  onSubmit,
  creatingLien,
}) => {
  const { grantors, entities, refreshEntities, refreshGrantors, document } = useUserContext();
  const [modalOpen, setModalOpen] = useState<null | { lookup: string; name: string; biometricType?: string }>(null);

  React.useEffect(() => {
    refreshGrantors();
    refreshEntities();
  }, []);

  // Get photographable grantors (not containers)
  const photographableGrantors = (grantors || []).filter((g: any) =>
    g.type !== 'trust' && g.type !== 'couple' && g.type !== 'company'
  );

  // Merge grantor data with entity transaction status
  const isRelease = document?.type === DOCUMENT_TYPE.LIEN_RELEASE || document?.type === 'LIEN_RELEASE';

  const displayEntities = photographableGrantors.map((g: any) => {
    const matchingEntity = (entities || []).find((e: any) =>
      e.grantorId === g.id || e.lookup === g.lookup
    );
    return {
      ...g,
      transactionAccepted: matchingEntity?.transactionAccepted || false,
      biometricType: isRelease ? 'compare' : (matchingEntity?.biometricType || undefined),
      lookup: g.lookup,
    };
  });

  const allParticipantsEnrolled = displayEntities
    .filter((g: any) => g.metadata?.claimantType === 'Participant')
    .every((g: any) => g.transactionAccepted);

  const step = 'biometrics';

  return (
    <div className="max-w-[700px] mx-auto flex flex-col items-center w-full">
      <EntityListComponent
        entities={displayEntities}
        step={step}
        onOpenModal={(lookup, name, biometricType) => setModalOpen({ lookup, name, biometricType })}
        modalOpen={modalOpen}
      />
      <CameraModal open={!!modalOpen} onClose={() => { setModalOpen(null); refreshEntities(); }} entityLookup={modalOpen?.lookup ?? null} entityName={modalOpen?.name} biometricType={modalOpen?.biometricType} />
      <div className="flex flex-row w-full max-w-[700px] gap-4 mt-4 mx-auto">
        <button
          type="button"
          className="flex-1 bg-[#7C8B9D] text-white font-plus-jakarta font-bold text-[15px] py-[10px] px-5 rounded-full hover:bg-[#485D7D] transition-colors"
          style={{ borderRadius: 60 }}
          onClick={onBack}
        >
          Back
        </button>
        <button
          type="button"
          className={`flex-1 bg-[#0B4BB6] text-white font-plus-jakarta font-bold text-[15px] py-[10px] px-5 rounded-full transition-colors ${displayEntities.length === 0 || !!creatingLien || !allParticipantsEnrolled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#23235a]'}`}
          style={{ borderRadius: 60 }}
          onClick={onSubmit}
          disabled={displayEntities.length === 0 || !!creatingLien || !allParticipantsEnrolled}
        >
          {creatingLien ? 'Creating Lien...' : 'Save & Continue'}
        </button>
      </div>
    </div>
  );
};

export default BiometricsCard;